import { FileCode2, Maximize2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ArtifactFullView } from '@/components/ArtifactFullView';
import { Button } from '@/components/ui/button';

import { ArtifactCarousel } from './ArtifactCarousel';

export interface Artifact {
    id: string;
    filename: string;
    html: string;
}

const SANDBOX = 'allow-scripts allow-same-origin allow-popups';
const PREVIEW_VIRTUAL_WIDTH = 1280;
const PREVIEW_MAX_HEIGHT = 170;
const HEIGHT_RESET_STYLE =
    '<style>html,body{min-height:0!important;height:auto!important;overflow:hidden!important}</style>';

function artifactMeta(artifact: Artifact) {
    const doc = new DOMParser().parseFromString(artifact.html, 'text/html');
    return {
        title:
            doc.querySelector('h1')?.textContent?.trim() ||
            doc.title ||
            artifact.filename,
        subtitle:
            doc.querySelector('header p, .subtitle')?.textContent?.trim() ?? ''
    };
}

function htmlBlobUrl(html: string) {
    return URL.createObjectURL(
        new Blob([html], { type: 'text/html;charset=utf-8' })
    );
}

function downloadArtifact(artifact: Artifact) {
    const url = htmlBlobUrl(artifact.html);
    const link = document.createElement('a');
    link.href = url;
    link.download = artifact.filename;
    link.click();
    URL.revokeObjectURL(url);
}

function withHeightReset(html: string) {
    return html.includes('</head>')
        ? html.replace('</head>', `${HEIGHT_RESET_STYLE}</head>`)
        : `${HEIGHT_RESET_STYLE}${html}`;
}

function ScaledPreview({ html, title }: { html: string; title: string }) {
    const outerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const [outerWidth, setOuterWidth] = useState(PREVIEW_VIRTUAL_WIDTH);
    const [contentHeight, setContentHeight] = useState(800);
    const srcDoc = useMemo(() => withHeightReset(html), [html]);

    useEffect(() => {
        const outer = outerRef.current;
        if (!outer) return;
        const observer = new ResizeObserver(() =>
            setOuterWidth(outer.clientWidth)
        );
        observer.observe(outer);
        return () => {
            observer.disconnect();
            resizeObserverRef.current?.disconnect();
        };
    }, []);

    const handleLoad = () => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        const measure = () => setContentHeight(doc.body.scrollHeight);
        measure();
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = new ResizeObserver(measure);
        resizeObserverRef.current.observe(doc.body);
    };

    const scale = Math.min(1, outerWidth / PREVIEW_VIRTUAL_WIDTH);

    return (
        <div
            ref={outerRef}
            className="relative w-full overflow-hidden"
            style={{
                height: Math.min(PREVIEW_MAX_HEIGHT, contentHeight * scale)
            }}
        >
            <iframe
                ref={iframeRef}
                title={title}
                srcDoc={srcDoc}
                sandbox={SANDBOX}
                onLoad={handleLoad}
                tabIndex={-1}
                aria-hidden
                className="block border-0 origin-top-left"
                style={{
                    width: PREVIEW_VIRTUAL_WIDTH,
                    height: contentHeight,
                    transform: `scale(${scale})`
                }}
            />
        </div>
    );
}

function ArtifactOverlay({
    artifacts,
    activeIndex,
    articleTitle,
    contentArea,
    onSelect,
    onClose
}: {
    artifacts: Artifact[];
    activeIndex: number;
    articleTitle: string;
    contentArea: HTMLElement | null;
    onSelect: (index: number) => void;
    onClose: () => void;
}) {
    const artifact = artifacts[activeIndex];
    const { title } = useMemo(() => artifactMeta(artifact), [artifact]);

    return (
        <ArtifactFullView
            title={title}
            subtitle={`From: ${articleTitle}`}
            html={artifact.html}
            frameKey={artifact.id}
            sandbox={SANDBOX}
            count={artifacts.length}
            activeIndex={activeIndex}
            container={contentArea}
            onSelect={onSelect}
            onClose={onClose}
            onDownload={() => downloadArtifact(artifact)}
            onOpenInNewTab={() =>
                window.open(htmlBlobUrl(artifact.html), '_blank', 'noopener')
            }
        />
    );
}

function ArtifactCard({
    artifact,
    onOpen
}: {
    artifact: Artifact;
    onOpen: () => void;
}) {
    const { title, subtitle } = useMemo(
        () => artifactMeta(artifact),
        [artifact]
    );

    return (
        <div className="group flex flex-col bg-background hover:shadow-sm border rounded-xl overflow-hidden transition-shadow">
            <button onClick={onOpen} className="relative text-left">
                <div className="pointer-events-none">
                    <ScaledPreview html={artifact.html} title={title} />
                </div>
            </button>
            <div className="flex items-center gap-3 py-3.5 pr-3 pl-4 border-t">
                <button
                    onClick={onOpen}
                    className="flex flex-1 items-center gap-2 min-w-0 text-left"
                >
                    <div className="flex flex-col flex-1 gap-0 min-w-0">
                        <p className="font-medium text-sm truncate">{title}</p>
                        <p className="text-muted-foreground text-xs truncate">
                            {subtitle || artifact.filename}
                        </p>
                    </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Expand"
                        title="Expand"
                        onClick={onOpen}
                    >
                        <Maximize2 />
                    </Button>
                </div>
            </div>
        </div>
    );
}

interface Props {
    artifacts: Artifact[];
    articleTitle: string;
}

export function ArtifactShelf({ artifacts, articleTitle }: Props) {
    const sectionRef = useRef<HTMLElement>(null);
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const closeOverlay = useCallback(() => setOpenIndex(null), []);

    if (artifacts.length === 0) return null;

    return (
        <section ref={sectionRef} className="mb-8">
            {/* <p className="mb-3 font-medium text-muted-foreground text-xs uppercase">
                Deliverables
            </p> */}
            <ArtifactCarousel
                count={artifacts.length}
                renderCard={(index) => (
                    <ArtifactCard
                        artifact={artifacts[index]}
                        onOpen={() => setOpenIndex(index)}
                    />
                )}
            />
            {openIndex !== null && artifacts[openIndex] && (
                <ArtifactOverlay
                    artifacts={artifacts}
                    activeIndex={openIndex}
                    articleTitle={articleTitle}
                    contentArea={sectionRef.current?.closest('main') ?? null}
                    onSelect={setOpenIndex}
                    onClose={closeOverlay}
                />
            )}
        </section>
    );
}
